'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ChevronDown, Menu, X } from 'lucide-react';
import type { Nav, NavMenu } from '@/lib/site/nav';
import NavItemLink from '@/components/site/NavItemLink';
import CountrySelect from '@/components/site/CountrySelect';
import { ButtonLink } from '@/components/site/Button';

// Desktop nav shows from 1000px (comp v5); 1000-1139px tightens item padding to 9px so everything fits at 1024px.
const TOP_ITEM =
  'flex h-11 items-center gap-1.5 rounded-lg px-3 min-[1000px]:max-[1139px]:px-[9px] min-[1000px]:max-[1139px]:text-[0.95rem] ' +
  'font-semibold text-[0.98rem] text-ink no-underline hover:bg-tile';
const FOCUS = 'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep';
const MEGA_LINK =
  'block py-1.5 text-ink no-underline hover:underline hover:decoration-marigold hover:decoration-2 hover:underline-offset-4 ' + FOCUS;
const ACC_TOP = `flex min-h-14 w-full items-center justify-between border-b border-line bg-transparent text-left font-display text-lg font-semibold text-ink no-underline ${FOCUS}`;
const slug = (label: string) => label.toLowerCase().replace(/[^a-z]+/g, '-');

type TopItem = NavMenu | 'christmas';

/**
 * The site header (client half; components/layout/Header.tsx builds the nav model on the server).
 * Desktop: disclosure buttons (aria-expanded; Escape and click-outside close) over mega-menu panels.
 * Phones: a menu button opens a sheet of accordions. Every panel is always rendered, hidden while closed,
 * so every link is a plain <a href> in the server HTML (crawlable without JS).
 */
export default function HeaderMenus({ nav }: { nav: Nav }) {
  const [open, setOpen] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);
  const [acc, setAcc] = useState<Record<string, boolean>>({});
  const desktopRef = useRef<HTMLElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!open && !sheet) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (open) { buttons.current[open]?.focus(); setOpen(null); }
      if (sheet) { burgerRef.current?.focus(); setSheet(false); }
    };
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (open && desktopRef.current && !desktopRef.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, sheet]);

  // A followed link closes whatever menu it sat in.
  const closeOnLink = (after: () => void) => (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) after();
  };

  // Display order (lib/site/nav): Our books, Kids, Gifts, Christmas (in season), Resources.
  const items: TopItem[] = [...nav.menus, ...(nav.christmas ? (['christmas'] as const) : []), nav.resources];

  return (
    // Sticky; on desktop the 34px utility strip scrolls away and the main bar stays. Honours the iPhone notch.
    <header className="sticky top-0 z-40 flex flex-col bg-paper pt-[env(safe-area-inset-top)] min-[1000px]:-top-[34px]">
      <div className="border-b border-line bg-paper">
        <div className="mx-auto flex h-16 max-w-page items-center gap-2.5 px-5 min-[600px]:gap-4 min-[720px]:px-8 min-[1000px]:h-[76px]">
          <Link
            href="/"
            aria-label="joonoo.art home"
            className={`inline-flex shrink-0 items-baseline font-display text-[1.3rem] font-semibold leading-none tracking-[-0.02em] text-ink no-underline min-[600px]:text-2xl ${FOCUS}`}
          >
            joonoo<span className="text-marigold-deep">.art</span>
          </Link>

          <nav ref={desktopRef} aria-label="Main" className="ml-3 hidden gap-0.5 min-[1000px]:flex min-[1140px]:ml-5">
            {items.map((m) => {
              if (m === 'christmas') {
                return (
                  <Link key="christmas" href={nav.christmas!.href} data-nav-top="christmas" className={`${TOP_ITEM} ${FOCUS}`}>
                    {nav.christmas!.label}
                  </Link>
                );
              }
              const id = `menu-${slug(m.label)}`;
              const isOpen = open === m.label;
              return (
                <div key={m.label} className="relative">
                  <button
                    ref={(el) => { buttons.current[m.label] = el; }}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={id}
                    onClick={() => setOpen(isOpen ? null : m.label)}
                    className={`${TOP_ITEM} ${FOCUS} ${isOpen ? 'bg-tile' : ''}`}
                  >
                    {m.label}
                    <ChevronDown aria-hidden="true" className={`h-3 w-3 transition-transform motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {/* The display class must follow `hidden`: a utility `grid` would beat the [hidden] rule. */}
                  <div
                    id={id}
                    hidden={!isOpen}
                    onClick={closeOnLink(() => setOpen(null))}
                    className={`${isOpen ? 'grid' : 'hidden'} absolute top-[52px] z-30 min-w-max grid-flow-col gap-7 rounded-card border border-line bg-paper-raised px-6 py-[22px] shadow-[var(--shadow-book)] ${m.label === 'Resources' ? 'right-0' : 'left-0'}`}
                  >
                    {m.groups.map((g, gi) => (
                      <div key={g.heading ?? gi}>
                        {g.heading && <p className="mb-2.5 text-[0.8125rem] font-semibold leading-tight text-ink-muted">{g.heading}</p>}
                        <ul className="m-0 grid list-none gap-0.5 p-0">
                          {g.links.map((l) => (
                            <li key={l.href + l.label}>
                              <NavItemLink link={l} source="menu" className={MEGA_LINK} />
                              {l.note && <span className="-mt-0.5 block max-w-[24ch] text-[0.8125rem] text-ink-muted">{l.note}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2.5 min-[600px]:gap-4">
            <ButtonLink
              href={nav.cta.href}
              event={nav.cta.event}
              className="whitespace-nowrap max-[599px]:px-3 max-[599px]:text-sm min-[1000px]:min-h-[48px] min-[1000px]:px-[22px]"
            >
              {nav.cta.label}
            </ButtonLink>
            <button
              ref={burgerRef}
              type="button"
              aria-controls="site-mobile-menu"
              aria-expanded={sheet}
              aria-label={sheet ? 'Close menu' : 'Open menu'}
              onClick={() => setSheet(!sheet)}
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-ink bg-transparent text-ink min-[1000px]:hidden ${FOCUS}`}
            >
              {sheet ? <X aria-hidden="true" className="h-5 w-5" /> : <Menu aria-hidden="true" className="h-[22px] w-[22px]" />}
            </button>
          </div>
        </div>
      </div>

      {/* Utility strip: after the bar in the HTML (main nav is tabbed first), shown above it on desktop. */}
      <nav aria-label="Utility" className="order-first hidden bg-ink text-[0.8125rem] text-paper min-[1000px]:block">
        <div className="mx-auto flex h-[34px] max-w-page items-center justify-end gap-[22px] px-8">
          {nav.utility.map((l) => (
            <NavItemLink
              key={l.href}
              link={l}
              source="menu"
              className="text-paper no-underline opacity-90 hover:underline hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper"
            />
          ))}
          <CountrySelect compact />
        </div>
      </nav>

      {/* Phone sheet: accordions, then the utility links and the main button. */}
      <div
        id="site-mobile-menu"
        hidden={!sheet}
        onClick={closeOnLink(() => setSheet(false))}
        className={`${sheet ? 'block' : 'hidden'} max-h-[calc(100dvh-64px-env(safe-area-inset-top))] overflow-y-auto border-b border-line bg-paper-raised min-[1000px]:hidden`}
      >
        <div className="mx-auto max-w-page px-5 pb-6 pt-2 min-[720px]:px-8">
          <nav aria-label="Main (phone)">
            {items.map((m) => {
              if (m === 'christmas') {
                return <Link key="christmas" href={nav.christmas!.href} className={ACC_TOP}>{nav.christmas!.label}</Link>;
              }
              const id = `acc-${slug(m.label)}`;
              const isOpen = !!acc[m.label];
              return (
                <div key={m.label}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={id}
                    onClick={() => setAcc((a) => ({ ...a, [m.label]: !isOpen }))}
                    className={ACC_TOP}
                  >
                    {m.label}
                    <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div id={id} hidden={!isOpen} className={`${isOpen ? 'block' : 'hidden'} border-b border-line pb-4`}>
                    {m.groups.map((g, gi) => (
                      <div key={g.heading ?? gi}>
                        {g.heading && <p className="mb-1 mt-2.5 text-[0.8125rem] font-semibold leading-none text-ink-muted">{g.heading}</p>}
                        <ul className={`m-0 grid list-none gap-x-3 p-0 ${m.label === 'Resources' ? 'grid-cols-1' : 'grid-cols-2'}`}>
                          {g.links.map((l) => (
                            <li key={l.href + l.label}>
                              <NavItemLink link={l} source="mobile-menu" className={`flex min-h-11 items-center text-ink no-underline ${FOCUS}`} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
          <div className="flex flex-wrap items-center gap-x-[22px] gap-y-1.5 pt-[18px] text-[0.9375rem] text-ink-soft">
            {nav.utility.map((l) => (
              <NavItemLink key={l.href} link={l} source="mobile-menu" className={`inline-flex min-h-11 items-center text-ink-soft no-underline hover:text-ink ${FOCUS}`} />
            ))}
            <CountrySelect />
          </div>
          <ButtonLink href={nav.cta.href} event={nav.cta.event} size="lg" className="mt-[18px] w-full">
            {nav.cta.label}
          </ButtonLink>
        </div>
      </div>
    </header>
  );
}
